import { addCreationData, addUpdateData, createId } from "@/lib/database";
import { ImportPlan } from "@/lib/automationImport";
import { logAutomationSavedWithNewBlocks } from "@/lib/automationAnalytics";
import { generic } from "@/repositories/generic";
import { firestore } from "@/services/firestore";
import { Automation, CreateAutomationDto } from "@/types/v2/Automation";
import { FirestoreError } from "firebase/firestore";

export const AutomationRepository = {
  path: (shopId: string) => `shops/${shopId}/automations`,
  create: async (shopId: string, automation: CreateAutomationDto) => {
    const automationId = createId();
    const automations = (await AutomationRepository.getAll(shopId)).sort(
      (a, b) => a.sortOrder - b.sortOrder
    );
    const createData: Automation = {
      id: automationId,
      sortOrder: automations.length + 1,
      shopId: shopId,
      enabled: true,
      name: `Automation-${automations.length + 1}`,
      ...automation,
    };
    const result = await generic.create<Automation>(
      addCreationData<Automation>(createData, "client"),
      `${AutomationRepository.path(shopId)}/${automationId}`
    );
    logAutomationSavedWithNewBlocks(shopId, createData);
    return result;
  },

  // `currentCount` is the caller's already-subscribed automation list length
  // (AutomationsConfiguration has this live via subscribeAll) — avoids a
  // redundant getAll() just to continue the sortOrder sequence.
  applyImportPlan: async (
    shopId: string,
    plan: ImportPlan,
    currentCount: number
  ) => {
    let nextSortOrder = plan.mode === "replaceAll" ? 1 : currentCount + 1;
    const ops: Parameters<typeof firestore.batchWrite>[0] = [];

    plan.deleteIds.forEach((id) =>
      ops.push({
        type: "delete",
        path: `${AutomationRepository.path(shopId)}/${id}`,
      })
    );
    plan.actions.forEach((action) => {
      // Parsed triggers/actions are structurally validated by zod and
      // catalog-checked by validateAutomationForImport, but their field
      // types are wider than Automation's — cast at this write boundary.
      if (action.kind === "create") {
        const id = createId();
        ops.push({
          type: "set",
          path: `${AutomationRepository.path(shopId)}/${id}`,
          data: addCreationData<Automation>(
            {
              id,
              shopId,
              sortOrder: nextSortOrder++,
              enabled: action.automation.enabled,
              // Firestore rejects undefined values — omit absent optionals.
              ...(action.automation.name !== undefined && {
                name: action.automation.name,
              }),
              ...(action.automation.description !== undefined && {
                description: action.automation.description,
              }),
              triggersConditional: action.automation.triggersConditional,
              triggers: action.automation.triggers as Automation["triggers"],
              actions: action.automation.actions as Automation["actions"],
            },
            "client"
          ),
        });
      } else if (action.kind === "update") {
        ops.push({
          type: "update",
          path: `${AutomationRepository.path(shopId)}/${action.id}`,
          data: addUpdateData({
            enabled: action.automation.enabled,
            ...(action.automation.name !== undefined && {
              name: action.automation.name,
            }),
            ...(action.automation.description !== undefined && {
              description: action.automation.description,
            }),
            triggersConditional: action.automation.triggersConditional,
            triggers: action.automation.triggers as Automation["triggers"],
            actions: action.automation.actions as Automation["actions"],
          }),
        });
      }
      // "reject" actions produce no write
    });

    return firestore.batchWrite(ops);
  },

  updateSortOrder: async (shopId: string, automations: Automation[]) =>
    Promise.all(
      automations.map((automation, index) => {
        const sortOrder = index + 1;
        if (automation.sortOrder === sortOrder) {
          return;
        }
        return generic.update<Automation>(
          { sortOrder: sortOrder },
          `${AutomationRepository.path(shopId)}/${automation.id}`
        );
      })
    ),
  moveUp: async (shopId: string, automationId: string) => {
    const automations = (await AutomationRepository.getAll(shopId)).sort(
      (a, b) => a.sortOrder - b.sortOrder
    );
    const automationCurrentIndex = automations.findIndex(
      (a) => a.id === automationId
    );
    if (automationCurrentIndex === 0) return;
    const automationPreviousIndex = automationCurrentIndex - 1;
    const automationPrevious = automations[automationPreviousIndex];
    automations[automationPreviousIndex] = automations[automationCurrentIndex];
    automations[automationCurrentIndex] = automationPrevious;
    return AutomationRepository.updateSortOrder(shopId, automations);
  },
  moveDown: async (shopId: string, automationId: string) => {
    const automations = (await AutomationRepository.getAll(shopId)).sort(
      (a, b) => a.sortOrder - b.sortOrder
    );
    const automationCurrentIndex = automations.findIndex(
      (a) => a.id === automationId
    );
    if (automationCurrentIndex === automations.length - 1) return;
    const automationNextIndex = automationCurrentIndex + 1;
    const automationNext = automations[automationNextIndex];
    automations[automationNextIndex] = automations[automationCurrentIndex];
    automations[automationCurrentIndex] = automationNext;
    return AutomationRepository.updateSortOrder(shopId, automations);
  },
  update: async (
    shopId: string,
    automationId: string,
    automationData: Partial<Automation>
  ) => {
    const result = await generic.update<Partial<Automation>>(
      { id: automationId, ...automationData },
      `${AutomationRepository.path(shopId)}/${automationId}`
    );
    logAutomationSavedWithNewBlocks(shopId, automationData);
    return result;
  },
  delete: (shopId: string, automationId: string) => {
    return generic.delete(automationId, AutomationRepository.path(shopId));
  },
  get: (shopId: string, automationId: string) =>
    generic.get<Automation>(automationId, AutomationRepository.path(shopId)),
  getAll: (shopId: string) => {
    return firestore.queryDocuments<Automation>(
      AutomationRepository.path(shopId),
      "_deleted",
      "!=",
      true,
      undefined,
      1000,
      "sortOrder",
      "asc"
    );
  },
  subscribeAll: (
    shopId: string,
    callback: (doc: Automation[] | undefined) => void,
    onError?: (error: FirestoreError) => void
  ) => {
    return firestore.subscribeCollectionWQueries<Automation>(
      AutomationRepository.path(shopId),
      (docs) => callback(docs.sort((a, b) => a.sortOrder - b.sortOrder)),
      [{ searchParam: "_deleted", opsString: "!=", equality: true }],
      undefined,
      1000,
      "sortOrder",
      "asc",
      onError
    );
  },
};
